import { Link } from "react-router-dom";
import { ADDRESS_LINE, LEGAL_NAME, ABN } from "../lib/business";
import { Facebook, Linkedin, Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: "Building Status Reports", path: "/services/building-status" },
    { name: "Building Inspection Reports", path: "/services/building-condition" },
    { name: "Pest Reports", path: "/services/pest" },
    { name: "Pool Remediation Reports", path: "/services/pool" },
    { name: "Plumbing Reports", path: "/services/plumbing" },
    { name: "Electrical Reports", path: "/services/electrical" },
  ];

  const serviceAreas = [
    { name: "Darwin", path: "/service-areas/darwin" },
    { name: "Palmerston", path: "/service-areas/palmerston" },
    { name: "Litchfield", path: "/service-areas/litchfield" },
    { name: "Wagait Beach & Cox Peninsula", path: "/service-areas/wagait" },
  ];

  const quickLinks = [
    { name: "For Home Buyers", path: "/inspections-for-buyers" },
    { name: "For Home Sellers", path: "/inspections-for-sellers" },
    { name: "About Us", path: "/about" },
    { name: "Blog", path: "/blog" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <footer className="bg-slate-900 text-slate-300" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <Link to="/" className="inline-block" aria-label="Aussie Inspections - Go to homepage">
                <span className="font-chivo text-2xl font-bold text-white">
                  Aussie Inspections
                </span>
              </Link>
            </div>
            <p className="text-sm leading-relaxed">
              Professional building and property inspection services in Darwin and
              throughout the Northern Territory.
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-chivo font-bold text-white mb-4">Services</h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-sm hover:text-primary transition-colors"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/ /g, '-')}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="font-chivo font-bold text-white mb-4">Service Areas</h3>
            <ul className="space-y-2">
              {serviceAreas.map((area) => (
                <li key={area.path}>
                  <Link
                    to={area.path}
                    className="text-sm hover:text-primary transition-colors"
                    data-testid={`footer-area-${area.name.toLowerCase().replace(/ /g, '-')}`}
                  >
                    {area.name}
                  </Link>
                </li>
              ))}
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm hover:text-primary transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/ /g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-chivo font-bold text-white mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <a
                  href="tel:+61413245519"
                  className="text-sm hover:text-primary transition-colors"
                  data-testid="footer-phone"
                >
                  0413 24 55 19
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:info@aussieinspections.com.au"
                  className="text-sm hover:text-primary transition-colors"
                  data-testid="footer-email"
                >
                  info@aussieinspections.com.au
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span className="text-sm">{ADDRESS_LINE}</span>
              </li>
            </ul>
            <div className="mt-6 flex items-center space-x-3">
              <a
                href="https://www.facebook.com/aussieinspections/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 bg-slate-800 hover:bg-primary rounded-full transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/aussie-inspections"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 bg-slate-800 hover:bg-primary rounded-full transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 text-center text-sm">
          <p className="mb-2">
            &copy; {currentYear} {LEGAL_NAME} (ABN {ABN}). All rights reserved. |
            Professional Building & Property Inspections Darwin NT
          </p>
          <p>
            <Link to="/privacy" className="hover:text-primary transition-colors">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}